// src/components/Navbar.jsx
import React, { useEffect, useState } from "react";
import { Bell, User, Headphones, LogOut } from "lucide-react";
import { useNavigate, useLocation } from "react-router";
import useAuthStore from "../store/useAuthStore";
import { getBuilderHomePath } from "../utils/builderNavigation";
import useConsultantAuthStore from "../store/consultant/useConsultantAuthStore";
import useNotificationStore from "../store/useNotificationStore";

const Navbar = ({ isOpen, setIsOpen, role = "builder" }) => {
  const navigate = useNavigate();
  const logoutBuilder = useAuthStore((state) => state.logoutBuilder);
  const userType = useAuthStore((state) => state.userType);
  const user = useAuthStore((state) => state.user);
  const { consultant } = useConsultantAuthStore();
  const { unreadCount, fetchUnreadCount } = useNotificationStore();

  // Tooltip state for name
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  const handleNav = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    if (role === "builder" || role === "incharge") {
      logoutBuilder();
      navigate("/");
    } else if (role === "consultant") {
      useConsultantAuthStore.getState().logout();
      navigate("/");
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      navigate("/");
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  // Get display name based on role
  const getDisplayName = () => {
    if (role === "consultant" && consultant) {
      return consultant.engineerName || consultant.name || "Consultant";
    }
    if (role === "incharge" && user) {
      return user.name || "Site Incharge";
    }
    if (role === "builder" && user) {
      return (
        user.builder?.engineer_name ||
        user.engineer_name ||
        user.name ||
        user.first_name ||
        "Builder"
      );
    }
    return role === "consultant" ? "Consultant" : "Builder";
  };

  const displayName = getDisplayName();
  const builderHomePath = getBuilderHomePath(user);

  /* -------------------------------------
     Active Logic for Navbar Icons
  --------------------------------------- */
  const location = useLocation();
  const isActive = (path) => location.pathname.startsWith(path);

  const getButtonClass = (path) => {
    return isActive(path)
      ? "w-7 h-7 md:w-8 md:h-8 bg-black rounded-full flex justify-center items-center hover:bg-gray-800 transition-colors"
      : "w-7 h-7 md:w-8 md:h-8 bg-blue-500 rounded-full flex justify-center items-center hover:bg-blue-600 transition-colors";
  };

  return (
    <div
      className="flex bg-white justify-between items-center px-1.5 xs:px-2 sm:px-3 md:px-4 font-poppins overflow-hidden"
      style={{ borderRadius: "8px", minHeight: "44px" }}
    >
      {/* 
        Custom Styles for the "Magic Border" Animation.
        We define a custom property for rotation and the keyframes.
      */}
      <style>{`
        @property --angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes rotate-gradient {
          to { --angle: 360deg; }
        }
        .btn-magic-border {
          /* Define the gradient colors here */
          --border-color-1: #3b82f6; /* Blue-500 */
          --border-color-2: #8b5cf6; /* Violet-500 */
          --border-color-3: #ec4899; /* Pink-500 */
          
          position: relative;
          background: white; /* Button background */
          z-index: 0;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        /* The rotating gradient layer */
        .btn-magic-border::before {
          content: '';
          position: absolute;
          z-index: -1;
          inset: -2px; /* Border width controls */
          background: conic-gradient(
            from var(--angle), 
            var(--border-color-1), 
            var(--border-color-2), 
            var(--border-color-3), 
            var(--border-color-2), 
            var(--border-color-1)
          );
          animation: rotate-gradient 3s linear infinite;
          border-radius: 9999px;
        }
        /* The inner background to hide the center of the gradient */
        .btn-magic-border::after {
          content: '';
          position: absolute;
          z-index: -1;
          inset: 2px; /* Match border width to create the "stroke" */
          background: linear-gradient(to right, #2563eb, #1d4ed8); /* Inner Button Color (Blue-600 to Blue-700) */
          border-radius: 9999px;
        }
        
        /* Fallback for browsers not supporting @property (like older Firefox) */
        @supports not (background: paint(something)) {
          .btn-magic-border::before {
             background: linear-gradient(45deg, #3b82f6, #ec4899);
             animation: none;
          }
        }
      `}</style>

      {/* Left side - Welcome message or title */}
      <div className="flex items-center gap-1 sm:gap-3 md:gap-4 min-w-0 flex-1">
        <div className="relative min-w-0">
          <div
            className="
              text-sm sm:text-base font-semibold text-gray-800
              truncate
              max-w-[80px] xxs:max-w-[100px] xs:max-w-[140px] sm:max-w-[200px] md:max-w-[260px] lg:max-w-[320px]
              min-w-0
              cursor-pointer
            "
            title={displayName ? `Hello, ${displayName}` : ""}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => role !== "consultant" && handleNav(builderHomePath)}
            onKeyDown={(e) => {
              if (role !== "consultant" && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                handleNav(builderHomePath);
              }
            }}
            tabIndex={0}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            aria-label={displayName ? `Hello, ${displayName}` : ""}
          >
            {displayName ? (
              <div className="flex items-baseline gap-1">
                <span className="hidden xs:inline">Hello,</span>
                <span className="inline xs:hidden">Hi,</span>
                <span className="truncate align-middle font-medium">{displayName}</span>
              </div>
            ) : (
              ""
            )}
          </div>
          {/* Tooltip for full name */}
          {displayName && showTooltip && (
            <div
              className="absolute left-0 z-50 mt-1 px-3 py-2 bg-gray-900 text-white text-sm rounded shadow-lg whitespace-pre-line"
              style={{
                minWidth: "max-content",
                maxWidth: "320px",
                whiteSpace: "pre-line",
                wordBreak: "break-word",
                top: "100%",
              }}
            >
              {displayName}
            </div>
          )}
        </div>
      </div>

      {/* Right Side Actions */}
      <div className="flex items-center space-x-1 xs:space-x-1.5 md:space-x-3 flex-shrink-0 ml-1">
        {/* Profile Button */}
        <button
          onClick={() => handleNav(role === "consultant" ? "/consultant/dashboard" : builderHomePath)}
          className={getButtonClass(role === "consultant" ? "/consultant/dashboard" : builderHomePath)}
        >
          <User color="#ffffff" size={16} className="md:w-4 md:h-4" />
        </button>

        {/* Notification Button */}
        <button
          onClick={() => handleNav(role === "consultant" ? "/consultant/notification" : "/builder/notification")}
          className={`${getButtonClass(role === "consultant" ? "/consultant/notification" : "/builder/notification")} relative`}
        >
          <Bell color="#ffffff" size={16} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center border-2 border-white px-1 leading-none z-10">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* Support Button */}
        <button
          onClick={() => handleNav(role === "consultant" ? "/consultant/support" : "/builder/support")}
          className={getButtonClass(role === "consultant" ? "/consultant/support" : "/builder/support")}
          title="Support"
        >
          <Headphones color="#ffffff" size={16} className="md:w-4 md:h-4" />
        </button>

        {/* Logout — next to support */}
        {(role === "builder" || role === "consultant" || role === "incharge") && (
          <button
            type="button"
            onClick={handleLogout}
            title="Logout"
            className="w-7 h-7 md:w-8 md:h-8 bg-red-500 rounded-full flex justify-center items-center hover:bg-red-600 transition-colors"
          >
            <LogOut color="#ffffff" size={16} className="md:w-4 md:h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Navbar;
